import { useEffect, useState, useCallback } from 'react'
import { Bug, ExternalLink, AlertTriangle, RefreshCw } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Advisory {
  id: string
  summary: string
  modified: string
  published: string
  aliases: string[]
  cve: string | null
  severity: string
  packages: string[]
  malicious: boolean
  primary_ecosystem: string
  url: string
}

interface FeedData {
  advisories: Advisory[]
  by_ecosystem: Record<string, number>
  malicious_count: number
  total: number
  days_back: number
}

export default function OsvFeedPage() {
  const [data, setData] = useState<FeedData | null>(null)
  const [loading, setLoading] = useState(true)
  const [days, setDays] = useState(14)
  const [eco, setEco] = useState<string>('all')
  const [maliciousOnly, setMaliciousOnly] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/sec/osv-feed', { params: { days_back: days, per_ecosystem: 30 } })
      setData(r.data)
    } finally { setLoading(false) }
  }, [days])

  useEffect(() => { fetchData() }, [fetchData])

  const filtered = (data?.advisories || [])
    .filter(a => eco === 'all' || a.primary_ecosystem === eco)
    .filter(a => !maliciousOnly || a.malicious)

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Bug size={24} className="text-rose-light" /> OSV.dev Cross-Ecosystem Feed
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            npm · PyPI · Go · Maven · RubyGems · crates.io · NuGet — broader than NVD for modern dev ecosystems.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={e => setDays(parseInt(e.target.value, 10))} className="input py-1.5 text-xs">
            <option value={7}>7d</option><option value={14}>14d</option><option value={30}>30d</option><option value={60}>60d</option>
          </select>
          <button type="button" onClick={fetchData} className="btn-ghost text-xs">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Total advisories" value={data.total} tone="blue" />
          <Tile label="Malicious / typosquat" value={data.malicious_count} tone={data.malicious_count > 0 ? 'rose' : 'emerald'} pulse={data.malicious_count > 0} />
          <Tile label="Window" value={`${days}d`} tone="amber" />
          <Tile label="Ecosystems" value={Object.keys(data.by_ecosystem).length} tone="emerald" />
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => setEco('all')}
          className={clsx('badge text-[10px] px-2 py-1', eco === 'all' ? 'badge-blue' : 'bg-surface-card text-text-muted border border-border')}>
          All
        </button>
        {data && Object.entries(data.by_ecosystem).map(([e, n]) => (
          <button key={e} type="button" onClick={() => setEco(e === eco ? 'all' : e)}
            className={clsx('badge text-[10px] px-2 py-1',
              eco === e ? 'badge-blue' : 'bg-surface-card text-text-muted border border-border')}>
            {e} <span className="text-text-muted ml-1">{n}</span>
          </button>
        ))}
        <label className="flex items-center gap-1.5 text-[11px] text-text-secondary ml-auto">
          <input type="checkbox" checked={maliciousOnly} onChange={e => setMaliciousOnly(e.target.checked)} />
          Malicious / typosquat only
        </label>
      </div>

      <div className="card p-3 overflow-x-auto">
        {filtered.length === 0 ? (
          <p className="text-center py-6 text-text-muted text-[11px] italic">No advisories match.</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
                <th className="text-left py-2 px-2">ID</th>
                <th className="text-left py-2 px-2">Summary</th>
                <th className="text-left py-2 px-2">Packages</th>
                <th className="text-left py-2 px-2">Modified</th>
                <th className="text-right py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 200).map(a => (
                <tr key={a.id} className={clsx('border-b border-border/50 hover:bg-surface-hover',
                  a.malicious && 'bg-rose/5')}>
                  <td className="py-1.5 px-2 font-mono text-[10px] text-blue-light whitespace-nowrap">
                    {a.malicious && <AlertTriangle size={10} className="inline text-rose mr-1" />}
                    {a.id}
                    {a.cve && <div className="text-[9px] text-text-muted">{a.cve}</div>}
                  </td>
                  <td className="py-1.5 px-2 text-text-secondary">{a.summary || '—'}</td>
                  <td className="py-1.5 px-2 font-mono text-[10px] text-text-muted">
                    {a.packages.slice(0, 3).join(', ')}{a.packages.length > 3 && ` +${a.packages.length - 3}`}
                  </td>
                  <td className="py-1.5 px-2 text-[10px] text-text-muted">{a.modified?.slice(0, 10)}</td>
                  <td className="py-1.5 px-2 text-right">
                    <a href={a.url} target="_blank" rel="noreferrer" className="text-blue-light hover:underline inline-flex items-center gap-1 text-[10px]">
                      open <ExternalLink size={9} />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function Tile({ label, value, tone, pulse }: { label: string; value: number | string; tone: string; pulse?: boolean }) {
  const t = tone === 'rose' ? 'text-rose' : tone === 'amber' ? 'text-amber-light' : tone === 'emerald' ? 'text-emerald' : 'text-blue-light'
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-bold mt-1 font-mono', t, pulse && 'animate-pulse')}>{value}</div>
    </div>
  )
}
